import { createLocalStorage, type StateStorage } from "./storage";

// Async-Persistenz (Phase 2): wird vom API-Worker (Cloudflare D1) bedient.
export interface AsyncRemote {
  pull(): Promise<string | null>;
  push(value: string): Promise<void>;
  clear(): Promise<void>;
}

// Local-first: die UI/Engine lesen synchron aus dem lokalen Speicher (sofortige UX,
// offline-fähig); Schreibvorgänge gehen synchron lokal + werden im Hintergrund an die
// API gepusht. So bleibt das synchrone StateStorage-Interface (und damit useLearningState
// und die Engine) unverändert — Phase 2 tauscht nur die Speicher-Implementierung.
export function createSyncedStorage(local: StateStorage, remote: AsyncRemote): StateStorage {
  return {
    load: () => local.load(),
    save: (value: string) => {
      local.save(value);
      // fire-and-forget; ein fehlgeschlagener Push (offline) darf die lokale UX nicht stören.
      Promise.resolve(remote.push(value)).catch(() => undefined);
    },
    clear: () => {
      local.clear();
      Promise.resolve(remote.clear()).catch(() => undefined);
    },
  };
}

// REST-Adapter gegen den Cloudflare-API-Worker. Cloudflare Access liefert die Identität
// per Cookie (credentials: include) — kein Token-Handling im Client nötig.
export function createApiStorage(baseUrl: string, fetchFn: typeof fetch = fetch): AsyncRemote {
  const url = `${baseUrl.replace(/\/$/, "")}/api/progress`;
  return {
    pull: async () => {
      const res = await fetchFn(url, { method: "GET", credentials: "include" });
      if (!res.ok) return null;
      return res.text();
    },
    push: async (value: string) => {
      await fetchFn(url, {
        method: "PUT",
        body: value,
        headers: { "content-type": "application/json" },
        credentials: "include",
      });
    },
    clear: async () => {
      await fetchFn(url, { method: "DELETE", credentials: "include" });
    },
  };
}

// Zentrale Storage-Factory: ohne API-Base lokale Persistenz (Phase 1), mit API-Base
// local-first + Background-Sync gegen den Cloudflare-Worker (Phase 2). Gesteuert über
// die Build-Env VITE_API_BASE — kein Code-Umbau beim Phasenwechsel.
export function createAppStorage(key: string, apiBase?: string): StateStorage {
  const local = createLocalStorage(key);
  return apiBase ? createSyncedStorage(local, createApiStorage(apiBase)) : local;
}

// "Fehler melden" -> POST an den Worker-Endpoint /api/feedback (existiert bereits, D1).
// Ohne API-Base (lokale Phase-1-Vorschau) wird nichts uebertragen -> false (UI zeigt Hinweis).
export async function submitFeedback(
  apiBase: string | undefined,
  body: string,
  exerciseId?: string,
  fetchFn: typeof fetch = fetch
): Promise<boolean> {
  if (!apiBase) return false;
  try {
    const res = await fetchFn(`${apiBase.replace(/\/$/, "")}/api/feedback`, {
      method: "POST",
      body: JSON.stringify({ body, exerciseId }),
      headers: { "content-type": "application/json" },
      credentials: "include",
    });
    return res.ok;
  } catch {
    return false;
  }
}
